import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useT } from '../../i18n/LanguageContext';
import useShippingRequest, { shippingAccessScope } from '../../hooks/useShippingRequest';
import { captureSession, isSessionCurrent } from '../../utils/authSession';
import distributorService from '../../services/distributorService';
import { parseCents, usd } from './cashMoney';
import {
  depositStateLabel,
  CASH_DEPOSIT_METHODS,
  cashDepositMethod,
  readCashDeposit,
  readCashDepositHistory,
  validCashDepositOptions,
  validCashCardFundingOptions,
  validCashCardFundingPolicy,
  sameCashDeposit,
  validCashFundingUser,
} from './cashDepositContract';
import CashDepositPayment from './CashDepositPayment';
import CashDepositHistory from './CashDepositHistory';
import { demoOrderingEnvironment } from '../../utils/demoOrderingEnvironment';

const unwrap = (response) => response?.data?.data;
export default function CashFunding({ readOnly = false, onFundsChanged }) {
  const auth = useAuth();
  const t = useT();
  const user = auth?.user;
  const distributorId = user?.distributor?._id || user?.distributor;
  if (!canAddCashFunds(user, readOnly)) return null;
  if (demoOrderingEnvironment())
    return (
      <fieldset className="me-cash me-cash-card">
        <legend>{t('Add funds to Tissue Bank')}</legend>
        <p>{t('Payments are disabled in this demo. Your sample balance stays unchanged.')}</p>
        <button type="button" disabled>
          {t('Add funds to Tissue Bank')}
        </button>
      </fieldset>
    );
  return (
    <FundingWorkspace
      key={`${user._id || user.id}:${distributorId}:${auth.sessionVersion || ''}`}
      distributorId={distributorId}
      onFundsChanged={onFundsChanged}
    />
  );
}
export function canAddCashFunds(user, readOnly = false) {
  return !readOnly && validCashFundingUser(user, user?.distributor?._id || user?.distributor);
}
function FundingWorkspace({ distributorId, onFundsChanged }) {
  const t = useT();
  const [options, setOptions] = useState(null),
    [receipt, setReceipt] = useState(null);
  const [history, setHistory] = useState(null),
    [page, setPage] = useState(1);
  const [amount, setAmount] = useState(''),
    [error, setError] = useState('');
  const [ready, setReady] = useState(false),
    [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false),
    [paymentLocked, setPaymentLocked] = useState(false);
  const [closeConfirmed, setCloseConfirmed] = useState(false),
    [retry, setRetry] = useState(0);
  const pending = useRef(false),
    intent = useRef(null),
    noticeCredit = useRef(null);
  const binding = useRef({ session: captureSession(), scope: shippingAccessScope() });
  const authorizedAccount = () => {
    try {
      return (
        isSessionCurrent(binding.current.session) &&
        binding.current.scope === shippingAccessScope() &&
        validCashFundingUser(JSON.parse(localStorage.getItem('user')), distributorId)
      );
    } catch {
      return false;
    }
  };
  const callback = useRef(onFundsChanged);
  callback.current = onFundsChanged;
  const { begin, version } = useShippingRequest(`cash-funding:${distributorId}`);
  const apply = (row) => {
    setReceipt(row);
    setUncertain(false);
    setCloseConfirmed(false);
    if (!row.open) setPaymentLocked(false);
    if (row.status === 'credited' && noticeCredit.current !== row._id) {
      noticeCredit.current = row._id;
      callback.current?.();
    }
  };
  const load = useCallback(async () => {
    if (pending.current || intent.current) return;
    const request = begin();
    setReady(false);
    setOptions(null);
    setError('');
    try {
      const [configuration, saved, entries] = await Promise.all([
        distributorService.cashDepositOptions({ signal: request.signal }),
        distributorService.currentCashDeposit({ signal: request.signal }),
        distributorService.cashDeposits(1, { signal: request.signal }),
      ]);
      if (!request.isCurrent()) return;
      const settings = unwrap(configuration),
        value = unwrap(saved);
      if (!validCashDepositOptions(settings, distributorId))
        throw new Error('Invalid funding availability');
      const current = value === null ? null : readCashDeposit(value);
      if (current && !current.open) throw new Error('Invalid current request');
      setOptions(settings);
      setReceipt(current);
      setHistory(readCashDepositHistory(unwrap(entries), 1));
      setPage(1);
      setReady(true);
      setPaymentLocked(Boolean(current?.attempt));
    } catch {
      if (request.isCurrent())
        setError('Cash funding could not load. Reload funding before creating a request.');
    }
  }, [begin, distributorId]);
  useEffect(() => {
    load();
  }, [load, version, retry]);
  useEffect(() => {
    const warn = (event) => {
      if (busy || uncertain) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [busy, uncertain]);
  const sameIntent = (value, frozen) =>
    value.requestKey === frozen.requestKey &&
    value.amountCents === frozen.amountCents &&
    cashDepositMethod(value) === 'card' &&
    value.authorizationVersion === 3 &&
    validCashCardFundingPolicy(value.fundingPolicy);
  const performPrepare = async () => {
    if (!authorizedAccount() || pending.current || !intent.current) return;
    const request = begin(),
      frozen = intent.current;
    pending.current = true;
    setBusy(true);
    setError('');
    try {
      const value = readCashDeposit(unwrap(await distributorService.prepareCashDeposit(frozen)));
      if (!request.isCurrent()) return;
      if (!sameIntent(value, frozen)) throw new Error('Unconfirmed funding request');
      intent.current = null;
      apply(value);
      setPaymentLocked(Boolean(value.attempt));
    } catch {
      if (request.isCurrent()) {
        setUncertain(true);
        setError('The funding request could not be confirmed. Recover it before continuing.');
      }
    } finally {
      pending.current = false;
      if (request.isCurrent()) setBusy(false);
    }
  };
  const prepare = (event) => {
    event.preventDefault();
    if (
      !authorizedAccount() ||
      !ready ||
      !validCashCardFundingOptions(options, distributorId) ||
      receipt?.open ||
      pending.current ||
      uncertain ||
      paymentLocked
    )
      return;
    const amountCents = parseCents(amount);
    if (
      amountCents === null ||
      amountCents < options.minAmountCents ||
      amountCents > options.maxAmountCents
    )
      return setError(
        'Enter a valid amount supported by this payment service, with at most two decimals.'
      );
    const requestKey = window.crypto?.randomUUID?.();
    if (!requestKey)
      return setError('A secure request key is unavailable. Reload before continuing.');
    intent.current = {
      requestKey,
      amountCents,
      currency: 'USD',
      method: 'card',
      fundingPolicyVersion: 2,
    };
    performPrepare();
  };
  const recover = async () => {
    if (pending.current || (!intent.current && !receipt)) return;
    const request = begin(),
      original = intent.current,
      frozen = receipt;
    pending.current = true;
    setBusy(true);
    setError('');
    try {
      const value = readCashDeposit(
        unwrap(
          await (original
            ? distributorService.recoverCashDeposit(original.requestKey)
            : distributorService.cashDeposit(frozen._id))
        )
      );
      if (!request.isCurrent()) return;
      if (original ? !sameIntent(value, original) : !sameCashDeposit(value, frozen))
        throw new Error('Unconfirmed request identity');
      intent.current = null;
      apply(value);
      setPaymentLocked(Boolean(value.attempt));
    } catch {
      if (request.isCurrent())
        setError(
          'This funding request is still unconfirmed. Keep the original request and contact MedEffects if recovery is unavailable.'
        );
    } finally {
      pending.current = false;
      if (request.isCurrent()) setBusy(false);
    }
  };
  const close = async () => {
    if (
      !authorizedAccount() ||
      pending.current ||
      uncertain ||
      !closeConfirmed ||
      !receipt?.open ||
      !(
        (receipt.status === 'prepared' && !receipt.attempt && !paymentLocked) ||
        receipt.status === 'declined'
      )
    )
      return;
    const request = begin(),
      frozen = receipt;
    pending.current = true;
    setBusy(true);
    setError('');
    try {
      const value = readCashDeposit(unwrap(await distributorService.closeCashDeposit(frozen._id)));
      if (!request.isCurrent()) return;
      if (!sameCashDeposit(value, frozen) || value.open) throw new Error('Unconfirmed closure');
      apply(value);
      setPaymentLocked(false);
      setAmount('');
    } catch {
      if (request.isCurrent()) {
        setUncertain(true);
        setError(
          'Request closure could not be confirmed. Recover this request before starting another.'
        );
      }
    } finally {
      pending.current = false;
      if (request.isCurrent()) setBusy(false);
    }
  };
  const loadPage = async (next) => {
    if (pending.current || uncertain) return;
    const request = begin();
    pending.current = true;
    setBusy(true);
    try {
      const rows = readCashDepositHistory(
        unwrap(await distributorService.cashDeposits(next, { signal: request.signal })),
        next
      );
      if (request.isCurrent()) {
        setHistory(rows);
        setPage(next);
      }
    } catch {
      if (request.isCurrent())
        setError('Funding requests could not load. Your payment request is unchanged.');
    } finally {
      pending.current = false;
      if (request.isCurrent()) setBusy(false);
    }
  };
  const fundingReady = validCashCardFundingOptions(options, distributorId);
  return (
    <fieldset className="me-cash-card" aria-label={t('Add funds to Tissue Bank')}>
      <legend>{t('Add funds to Tissue Bank')}</legend>
      <p className="me-cash-heading">
        {t('Tissue Bank funding is by credit card only. Customer resale prices are separate.')}
      </p>
      {error && <p role="alert">{t(error)}</p>}
      {!ready && !error && <p role="status">{t('Checking funding availability...')}</p>}
      {!ready && error && (
        <button type="button" disabled={busy} onClick={() => setRetry((value) => value + 1)}>
          {t('Reload funding')}
        </button>
      )}
      {ready && !fundingReady && (
        <>
          <p className="me-cash-note">
            {t('Credit card funding will be available after setup and verification.')}
          </p>
          <button type="button" disabled>
            {t('Add funds to Tissue Bank')}
          </button>
        </>
      )}
      {uncertain && (
        <div className="me-cash-actions">
          <button type="button" disabled={busy} onClick={recover}>
            {t('Recover funding request')}
          </button>
          {intent.current && (
            <button type="button" disabled={busy} onClick={performPrepare}>
              {t('Retry original preparation')}
            </button>
          )}
        </div>
      )}
      {ready && fundingReady && !receipt?.open && !uncertain && (
        <form onSubmit={prepare}>
          <label>
            {t('Amount to add (USD)')}
            <input
              value={amount}
              inputMode="decimal"
              onChange={(event) => setAmount(event.target.value)}
              disabled={busy || paymentLocked}
            />
          </label>
          <p>
            {t(
              'Preparing a request does not charge your card. Review the card charge and full Tissue Bank credit before authorizing.'
            )}
          </p>
          <p>
            {t(
              'MedEffects covers the card processing fee. No processing fee is added to your card charge.'
            )}
          </p>
          <div className="me-cash-actions">
            <button className="me-cash-primary" type="submit" disabled={busy || paymentLocked}>
              {t('Review card deposit')}
            </button>
          </div>
        </form>
      )}
      {receipt && (
        <div className="me-cash-details">
          <div className="me-cash-heading">
            <h3>{usd(receipt.amountCents)}</h3>
            <span role="status">{t(depositStateLabel(receipt))}</span>
          </div>
          <p>{t('Request reference: {reference}', { reference: receipt._id })}</p>
          <p>{t(CASH_DEPOSIT_METHODS[cashDepositMethod(receipt)])}</p>
          {receipt.status !== 'credited' && (
            <p>{t('This request has not added funds to your available balance.')}</p>
          )}
          {!uncertain && (
            <CashDepositPayment
              key={receipt._id}
              deposit={receipt}
              distributorId={distributorId}
              onUpdated={apply}
              onLockedChange={setPaymentLocked}
              disabled={busy}
            />
          )}
          {receipt.open &&
            !uncertain &&
            (receipt.status === 'declined' ||
              (receipt.status === 'prepared' && !paymentLocked)) && (
              <details className="me-cash-details">
                <summary>{t('Close this funding request')}</summary>
                <label className="me-cash-authorize">
                  <input
                    type="checkbox"
                    checked={closeConfirmed}
                    onChange={(event) => setCloseConfirmed(event.target.checked)}
                    disabled={busy}
                  />
                  {t('Close this unsubmitted or declined request without sending another payment.')}
                </label>
                <button type="button" disabled={busy || !closeConfirmed} onClick={close}>
                  {t('Confirm request closure')}
                </button>
              </details>
            )}
        </div>
      )}
      <CashDepositHistory
        history={history}
        page={page}
        busy={busy || uncertain}
        onPage={loadPage}
      />
    </fieldset>
  );
}
